import React, { useMemo } from 'react';
import { Form, Input, Radio, Checkbox, Tooltip, Space, FormInstance } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import StateSelect from 'components/form/StateSelect';
import { AdditionalPracticeFormFieldEnum } from 'pages/CreateUniProfilePage/types';
import s from './s.module.less';
import AutoAddressInput from 'components/AutoAddressInput';
import useSettings from 'hooks/useSettings';
import { sortByProperty } from 'utils/form';
import { EnumFields } from 'types/enumerationData';
import type { IFormData } from '../PracticeAddress';

interface OptionType {
    value: string | number;
    label: React.ReactNode;
}

interface AddressCardProps {
    name: number; // index from Form.List
    restField: Record<string, any>;
    addressTypeOptions: OptionType[]; // Use the simple OptionType
    handleAddressChange: (index: number) => void;
    isSelectedDefault: boolean; // Is this the currently selected default address?
    onSelectDefault: (index: number) => void; // Callback to select this as default
    form: FormInstance<IFormData>;
}

const AddressCard: React.FC<AddressCardProps> = ({
    name,
    restField,
    addressTypeOptions,
    handleAddressChange,
    isSelectedDefault,
    onSelectDefault,
    form,
}) => {
    const [setting] = useSettings();
    const stateOption = sortByProperty(setting[EnumFields.STATE] || [], 'label');

    if (!setting || !setting[EnumFields.STATE]) {
        return null;
    }

    return (
        <div className={s.addressCard}>
            <Form.Item
                {...restField}
                label={<div className={s.formLabel}>Address type</div>}
                name={[name, AdditionalPracticeFormFieldEnum.ADDRESS_PUBLIC_TYPE]}
                rules={[{ required: true, message: 'Address type is a required field' }]}
                className={s.addressTypeItem}
            >
                <Radio.Group onChange={() => handleAddressChange(name)}>
                    <Space direction="vertical">
                        {addressTypeOptions?.map((e) => (
                            <Radio key={e.value} value={e.value}>{e.label}</Radio>
                        ))}
                    </Space>
                </Radio.Group>
            </Form.Item>

            <Form.Item
                {...restField}
                label={<div className={s.formLabel}>Address</div>}
                name={[name, AdditionalPracticeFormFieldEnum.ADDRESS]}
                validateFirst
                rules={[{ required: true, message: 'Address is a required field' }]}
            >
                <AutoAddressInput
                    placeholder="Street address"
                    onChange={() => handleAddressChange(name)}
                    addCityToAddress
                    onStateChange={(val) => {
                        const targetState = stateOption.find((item) => item.value === val || item.label === val);
                        if (targetState) {
                            form.setFieldsValue({
                                addressList: form.getFieldValue('addressList').map((item: any, idx: number) => {
                                    if (idx === name) {
                                        return { ...item, practiceState: targetState.value };
                                    }
                                    return item;
                                }),
                            });
                        }
                    }}
                    onCityChange={(val) => {
                        form.setFieldsValue({
                            addressList: form.getFieldValue('addressList').map((item: any, idx: number) => {
                                if (idx === name) {
                                    return { ...item, practiceCity: val };
                                }
                                return item;
                            }),
                        });
                    }}
                    onPostcodeChange={(val) => {
                        form.setFieldsValue({
                            addressList: form.getFieldValue('addressList').map((item: any, idx: number) => {
                                if (idx === name) {
                                    return { ...item, practiceZip: val };
                                }
                                return item;
                            }),
                        });
                    }}
                />
            </Form.Item>

            <div className={s.cityStateZipRow}>
                <Form.Item
                    {...restField}
                    label={<div className={s.formLabel}>City</div>}
                    name={[name, AdditionalPracticeFormFieldEnum.CITY]}
                    validateFirst
                    rules={[{ required: true, message: 'City is a required field' }]}
                    className={s.cityItem}
                >
                    <Input type="text" placeholder="City" onChange={() => handleAddressChange(name)} />
                </Form.Item>
                <Form.Item
                    {...restField}
                    label={<div className={s.formLabel}>State</div>}
                    name={[name, AdditionalPracticeFormFieldEnum.STATE]}
                    validateFirst
                    rules={[{ required: true, message: 'State is a required field' }]}
                    className={s.stateItem}
                >
                    <StateSelect onChange={() => handleAddressChange(name)} />
                </Form.Item>
                <Form.Item
                    {...restField}
                    label={<div className={s.formLabel}>ZIP code</div>}
                    name={[name, AdditionalPracticeFormFieldEnum.ZIP_CODE]}
                    validateFirst
                    rules={[{ required: true, message: 'Zip code is a required field' }]}
                    className={s.zipItem}
                >
                    <Input type="text" placeholder="ZIP code" onChange={() => handleAddressChange(name)} />
                </Form.Item>
            </div>

            <Form.Item
                {...restField}
                className={s.checkboxItem}
            >
                <Radio
                    checked={isSelectedDefault}
                    onChange={() => onSelectDefault(name)}
                >
                    Make this my default practice address
                    <Tooltip title="Default practice address will be used as your default address in Practice Front, Service and Channels.">
                        <QuestionCircleOutlined className={s.tooltipIcon} />
                    </Tooltip>
                </Radio>
            </Form.Item>

            <Form.Item
                {...restField}
                name={[name, AdditionalPracticeFormFieldEnum.AS_MAILING]}
                valuePropName="checked"
                className={s.checkboxItem}
            >
                <Checkbox onChange={() => handleAddressChange(name)}>Set as a mailing address</Checkbox>
            </Form.Item>

        </div>
    );
};

export default AddressCard;
